/**
 * Growth Stages — Learner-facing mastery visualization (groei-narratief)
 *
 * Maps a 0–100 mastery score to a 6-level growth ladder with emoji, label
 * and color. Calibrated against the adaptive engine ceiling (95+ = Meester!).
 *
 * SCOPE — learner shell only:
 *   • Voortgang / achievements page
 *   • GrowthStageIcon (and downstream celebrations)
 *   • Session summary / celebration moments
 *   • MasteryRing emoji + color display in learner contexts
 *
 * NOT used by parent or teacher portals. Those portals consume the
 * 4-level adult dashboard vocabulary defined in
 * `src/portals/shared/masteryBands.ts` (Aandacht / Aan het groeien /
 * Goed op weg / Sterk). See `docs/architecture/THRESHOLD_SYSTEMS.md`
 * and `docs/specs/MASTERY_DISPLAY_LANGUAGE_2026.md` for the full picture.
 *
 * Part of the Mastery domain (moved from utils/growthStages.ts).
 */

export interface GrowthStage {
  emoji: string;
  label: string;
  level: number;
  color: string;
}
export function getGrowthStage(masteryScore: number): GrowthStage {
  if (masteryScore <= 0) {
    return {
      emoji: '🔘',
      label: 'Nieuw',
      level: 0,
      color: 'bg-slate-300'
    };
  }
  if (masteryScore < 25) {
    return {
      emoji: '⭐',
      label: 'Net begonnen',
      level: 1,
      color: 'bg-emerald-300'
    };
  }
  if (masteryScore < 50) {
    return {
      emoji: '💫',
      label: 'Groeit',
      level: 2,
      color: 'bg-emerald-400'
    };
  }
  if (masteryScore < 80) {
    return {
      emoji: '🔥',
      label: 'Goed op weg',
      level: 3,
      color: 'bg-emerald-500'
    };
  }
  if (masteryScore < 95) {
    return {
      emoji: '🏆',
      label: 'Sterk',
      level: 4,
      color: 'bg-amber-400'
    };
  }
  return {
    emoji: '👑',
    label: 'Meester!',
    level: 5,
    color: 'bg-amber-500'
  };
}